// Horizontal swipe between feed tabs
// -----------------------------------
// The tab bar works by tapping, which is fine on a desktop pointer and wrong
// on a phone: every other surface in the app moves under your thumb, so a row
// of tabs that only responds to precise taps reads as broken rather than as
// deliberate.
//
// This adds the gesture without taking anything away. Tapping still works
// exactly as before.
//
// Deliberately built on raw touch events rather than a gesture library. The
// feed tabs render four completely different scrolling surfaces, and the one
// thing this must never do is interfere with the vertical scroll inside them.
// Listening passively and never calling preventDefault guarantees that: the
// browser keeps full control of scrolling, and this only ever decides, after
// the fact, whether what happened was a horizontal flick.

import { useCallback, useMemo, useRef } from 'react';

/** Minimum horizontal travel, in px, for a slow drag to count. */
const COMMIT_DISTANCE = 60;
/** A fast flick commits on less distance. px/ms. */
const FLICK_VELOCITY = 0.4;
const FLICK_DISTANCE = 30;
/**
 * How much more horizontal than vertical the gesture must be.
 *
 * A thumb swiping down a feed rarely travels in a straight line, and the cost
 * of getting this wrong is asymmetric: a missed tab swipe is a shrug, while a
 * tab that changes because someone scrolled is a bug.
 */
const HORIZONTAL_BIAS = 1.2;

/**
 * Opt-out marker. Any element carrying `data-no-tab-swipe` swallows gestures
 * that start inside it, for surfaces with horizontal gestures of their own.
 */
export const NO_TAB_SWIPE_ATTR = 'data-no-tab-swipe';

export interface TabSwipeOptions<T extends string> {
  /** Tabs in the order they appear on screen, left to right. */
  tabs: readonly T[];
  activeTab: T;
  onChange: (tab: T) => void;
  /** Suppresses the gesture entirely, e.g. while an overlay is open. */
  disabled?: boolean;
}

interface TouchOrigin {
  x: number;
  y: number;
  at: number;
  /** Set when the gesture began somewhere that opted out. */
  ignored: boolean;
}

export function useTabSwipe<T extends string>({
  tabs,
  activeTab,
  onChange,
  disabled = false,
}: TabSwipeOptions<T>) {
  const origin = useRef<TouchOrigin | null>(null);

  // Only where a finger is the primary input. On a desktop pointer the tabs
  // are already a click target and a horizontal mouse drag means selection,
  // not navigation.
  const isTouchPrimary = useMemo(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(pointer: coarse)').matches,
    []
  );

  const active = isTouchPrimary && !disabled;

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!active || e.touches.length !== 1) {
        origin.current = null;
        return;
      }
      const touch = e.touches[0];
      const target = e.target as HTMLElement | null;
      origin.current = {
        x: touch.clientX,
        y: touch.clientY,
        // Date.now rather than the event's own timeStamp: browsers have
        // historically disagreed on whether that is an epoch or a
        // page-relative value, and mixing the two makes velocity nonsense.
        at: Date.now(),
        ignored: Boolean(target?.closest?.(`[${NO_TAB_SWIPE_ATTR}]`)),
      };
    },
    [active]
  );

  // A second finger means a pinch or some other gesture, never a tab swipe.
  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length > 1) origin.current = null;
  }, []);

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const start = origin.current;
      origin.current = null;
      if (!start || start.ignored || !active) return;

      const touch = e.changedTouches[0];
      if (!touch) return;

      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      const distance = Math.abs(dx);
      const elapsed = Math.max(Date.now() - start.at, 1);
      const velocity = distance / elapsed;

      if (distance < Math.abs(dy) * HORIZONTAL_BIAS) return;

      const committed =
        distance >= COMMIT_DISTANCE ||
        (velocity >= FLICK_VELOCITY && distance >= FLICK_DISTANCE);
      if (!committed) return;

      const current = tabs.indexOf(activeTab);
      if (current === -1) return;

      // Dragging left pulls the next tab in from the right, which is the
      // direction the content appears to travel.
      const next = dx < 0 ? current + 1 : current - 1;
      // The ends are walls, not a carousel. Wrapping from the last tab to the
      // first loses your place in a row you can see all of.
      if (next < 0 || next >= tabs.length) return;

      onChange(tabs[next]);
    },
    [active, tabs, activeTab, onChange]
  );

  const onTouchCancel = useCallback(() => {
    origin.current = null;
  }, []);

  return {
    /** Spread onto the element that should listen. */
    swipeHandlers: { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel },
    /** Whether the gesture is live, for showing an affordance. */
    isSwipeEnabled: active,
  };
}
